import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';
import { ATTENDANCE_STATUS_FILTER_OPTIONS } from '../../utils/attendanceTableSort';

export default function ColumnStatusFilterHeader({
  label,
  selectedStatuses = [],
  onStatusFilterChange,
  showLateOnly = false,
  onShowLateOnlyChange,
  showOvertimeOnly = false,
  onShowOvertimeOnlyChange,
  showEarlyLeaveOnly = false,
  onShowEarlyLeaveOnlyChange,
  variant = 'dark',
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const activeExtraFilters = [
    showLateOnly,
    showOvertimeOnly,
    showEarlyLeaveOnly,
  ].filter(Boolean).length;
  const activeCount = selectedStatuses.length + activeExtraFilters;

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeMenu]);

  const handleToggle = (event) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleStatusToggle = (value) => {
    if (selectedStatuses.includes(value)) {
      onStatusFilterChange(selectedStatuses.filter((item) => item !== value));
      return;
    }
    onStatusFilterChange([...selectedStatuses, value]);
  };

  const metricFilters = [
    {
      key: 'late',
      label: 'Late check-in only',
      checked: showLateOnly,
      onChange: onShowLateOnlyChange,
    },
    {
      key: 'overtime',
      label: 'Overtime only',
      checked: showOvertimeOnly,
      onChange: onShowOvertimeOnlyChange,
    },
    {
      key: 'earlyLeave',
      label: 'Early leave only',
      checked: showEarlyLeaveOnly,
      onChange: onShowEarlyLeaveOnlyChange,
    },
  ];

  const headerTextClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  const chevronClass = variant === 'dark' ? 'text-white' : 'text-gray-700';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-1 whitespace-nowrap font-semibold ${headerTextClass}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{label}</span>
        {activeCount > 0 && (
          <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-white/20 px-1 text-[10px] font-bold">
            {activeCount}
          </span>
        )}
        <IoMdArrowDropdown className={`text-base shrink-0 ${chevronClass}`} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[14rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          <div className="border-b border-gray-100 py-1">
            {ATTENDANCE_STATUS_FILTER_OPTIONS.map((option) => {
              const selected = selectedStatuses.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={selected}
                  onClick={() => handleStatusToggle(option.value)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 ${
                    selected ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className="w-4 shrink-0 text-gray-600">
                    {selected ? '✓' : ''}
                  </span>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          <div className="py-1">
            {metricFilters.map((filter) => (
              <label
                key={filter.key}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  checked={filter.checked}
                  onChange={(event) => filter.onChange(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#674936] focus:ring-[#674936]"
                />
                <span>{filter.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
